/** Shared distribution statistics for the Barrier Effectiveness panels.
 *  Pure functions — no React, no chart deps — so they're easy to unit-test and
 *  reuse across the Failure Exposure, Effectiveness Rate, Demand Coverage and
 *  MTBF failure-gap distributions. */

/** Five-number summary (+ mean) shared by every distribution panel. */
export interface FiveNumberSummary {
    n: number;
    mean: number;
    median: number;
    min: number;
    q1: number;
    q3: number;
    max: number;
}

/** A single histogram bucket. */
export interface DistributionBin {
    start: number;
    end: number;
    count: number;
}

export interface Distribution {
    /** Ascending-sorted input values (used for the empirical CDF). */
    values: number[];
    bins: DistributionBin[];
    stats: FiveNumberSummary;
}

/** Linear-interpolation percentile (a.k.a. type-7 / Excel PERCENTILE.INC) on an
 *  ASCENDING-sorted array. `percentile(sorted, 0.5)` equals the usual median
 *  (average of the two middle values for even n), so all quartiles are consistent. */
export const percentile = (sorted: number[], p: number): number => {
    if (sorted.length === 0) return NaN;
    if (sorted.length === 1) return sorted[0];
    const idx = (sorted.length - 1) * p;
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
};

/** Min / Q1 / median / Q3 / max + mean from an ASCENDING-sorted array. */
export const fiveNumberSummary = (sorted: number[]): FiveNumberSummary => ({
    n: sorted.length,
    mean: sorted.reduce((s, v) => s + v, 0) / sorted.length,
    median: percentile(sorted, 0.5),
    min: sorted[0],
    q1: percentile(sorted, 0.25),
    q3: percentile(sorted, 0.75),
    max: sorted[sorted.length - 1],
});

/** Builds a fixed-width histogram (bins start at 0) plus the five-number summary
 *  from raw values. Pass `binCount` to fix the number of bins (e.g. 10 for a
 *  0–100 % axis); omit it to derive the count from the data's max
 *  (`ceil(max / binWidth)`). Returns `null` for an empty input. */
export const buildDistribution = (
    rawValues: number[],
    binWidth: number,
    binCount?: number,
): Distribution | null => {
    if (rawValues.length === 0) return null;
    const values = [...rawValues].sort((a, b) => a - b);
    const stats = fiveNumberSummary(values);
    const count = binCount ?? Math.max(1, Math.ceil(stats.max / binWidth));
    const bins: DistributionBin[] = [];
    for (let i = 0; i < count; i++) {
        bins.push({start: i * binWidth, end: (i + 1) * binWidth, count: 0});
    }
    for (const v of values) {
        const idx = Math.min(Math.floor(v / binWidth), count - 1);
        bins[idx].count++;
    }
    return {values, bins, stats};
};
